import * as moment from 'moment-timezone';
import { PropertyTypes } from '@models/common.models';
import SellersCoverageIncludedStates from '@constants/SellersCoverageIncludedStates';

export const smallMoneyMax = 214748.3647;

export const hasValue = (val: string | number): boolean => {
  if ((typeof val !== 'number' && !val) || val === '-1') {
    return false;
  } else if (typeof val === 'number') {
    if (+val < 0) {
      return false;
    }

    // edge cases NaN & Infinity
    var temp = String(val);
    if (temp === 'NaN' || temp === 'Infinity') {
      return false;
    }
    return true;
  }
  var valString = val.toString().trim();

  var isValid = valString.length > 0;
  return isValid;
};

export const isValidCity = (val: string) => {
  return val ? /^[a-zA-Z]+([-' ][a-zA-Z]+){0,}$/.test(val) : true;
};

export const isValidCountry = (val: string) => {
  return val ? /^[a-zA-Z]+([-' ][a-zA-Z]+){0,}$/.test(val) : true;
};

export const isValidStateProvince = (val: string) => {
  return val ? /^[a-zA-Z]+([-' ][a-zA-Z]+){0,}$/.test(val) : true;
};

export const isValidEmail = (val: string): boolean => {
  const regexp = new RegExp(
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  );
  return val ? regexp.test(String(val).toLowerCase()) : true;
};

export const isValidPhoneNumber = (val: string): boolean => {
  const regexp = new RegExp(/^([0-9]){3}-[0-9]{3}-[0-9]{4}$/);

  return regexp.test(String(val)) || !hasValue(val);
};

export const isValidFaxNumber = (val: string): boolean => {
  const regexp = new RegExp(/^([0-9]){3}-[0-9]{3}-[0-9]{4}$/);

  return regexp.test(String(val)) || !hasValue(val);
};

export const hasUpperCase = (val: string): boolean => {
  return /[A-Z]/.test(val);
};

export const hasLowerCase = (val: string): boolean => {
  return /[a-z]/.test(val);
};

export const hasNumber = (val: string): boolean => {
  if (!val) {
    return true;
  }
  return /\d/.test(val);
};

export const hasNonAlphaNumeric = (val: string): boolean => {
  return /\W/.test(val);
};

export const isValidPassword = (val: string): boolean => {
  if (!val) {
    return true;
  }

  var minLength8 = val.length >= 8;
  var combo = +hasUpperCase(val) + +hasLowerCase(val) + +hasNumber(val) + +hasNonAlphaNumeric(val);
  var validCombo = combo >= 3;
  return minLength8 && validCombo;
};

export const notSame = (val1: string, val2: string): boolean => {
  if (!val1 && !val2) {
    return true;
  }

  return val1 !== val2;
};

export function isValidZip(val: string): boolean {
  const regexp = new RegExp(/^[0-9]{5}$/); // new RegExp(/^[0-9]{5}(-?([0-9]{4})?)$/);
  return val ? regexp.test(val) : true;
}

/**
 * A function that tests if a value contains alphabetics only
 * @param val - the value to test.
 */
export const hasAlpha = (val: string): boolean => {
  return val ? /[a-zA-Z]/.test(val) : true;
};

export const no3Chars = (email: string, password: string) => {
  if (!email || !password) {
    return true;
  }
  var lowerEmail = email.toLowerCase();
  var lowerPass = password.toLowerCase();

  if (lowerEmail.length < 3 || lowerPass.length < 3) {
    return true;
  }

  var checkLength = Math.min(email.length - 3, password.length - 3);

  for (var i = 0; i <= checkLength; i++) {
    var checkFor = lowerPass.substring(i, i + 3);
    if (lowerEmail.indexOf(checkFor) > -1) {
      return false;
    }
  }
  return true;
};

export const isAlpha = (val: string): boolean => {
  return val ? /^[^0-9]*$/.test(val) : true;
};

export const isNumeric = (val: string): boolean => {
  return val ? /^[0-9]*$/.test(val) : true;
};

export const isFederalTaxId = (val: string): boolean => {
  return val ? /^([0-9]{2}[-]{1}[0-9]{7})|([0-9]{3}[-]{1}[0-9]{2}[-]{1}[0-9]{4})$/.test(val) : true;
};

export const isDecimal = (val: string): boolean => {
  return val ? /^[0-9]*\.?[0-9]*$/.test(val) : true;
};

export const isIntegerGreaterThanZero = (val: string): boolean => {
  return val ? /^[1-9]\d*$/.test(val) : true;
};

export const hasDateValue = <T>(val: T): boolean => {
  if (val && typeof val === 'object') {
    return Object.keys(val).length > 0;
  } else if (val && typeof val === 'string') {
    var date = moment(val);
    return date.isValid();
  }
  return false;
};

export const hasOnlyAlphaNumeric = (val: string): boolean => {
  return !hasNonAlphaNumeric(val);
};

export const isNotOverMaxLength = (val: string, maxLength: number): boolean => {
  if (!val) {
    return true;
  }

  return val.length <= maxLength;
};

export const isNotOverLength10 = (val: string): boolean => {
  return isNotOverMaxLength(val, 10);
};

export const isNotOverLength15 = (val: string): boolean => {
  return isNotOverMaxLength(val, 15);
};

export const isNotOverLength20 = (val: string): boolean => {
  return isNotOverMaxLength(val, 20);
};

export const isNotOverLength255 = (val: string): boolean => {
  return isNotOverMaxLength(val, 255);
};

export function isValidCreditCardNumber(input: string): boolean {
  return !input || /^[0-9]{13,19}$/.test(input);
}

export function isValidSecurityCode(input: string): boolean {
  return !input || /^[0-9]{3,4}$/.test(input);
}

export function isValidRoutingNumber(input: string): boolean {
  return !input || /^[0-9]{9}$/.test(input);
}

export function isValidAccountNumber(input: string): boolean {
  return !input || /^[0-9]$/.test(input);
}

export const isValidDate = (val: string): boolean => {
  var date = moment(val, 'MM/DD/YYYY', true);
  return date.isValid();
};

export const isNotFutureYear = (val: string): boolean => {
  var date = moment(val, 'YYYY', true);
  var currentDate = moment();
  return date.isSameOrBefore(currentDate, 'year');
};

export const isPropertyTypeElligibleForSellersCoverage = (propertyTypeId: number) => {
  return (
    propertyTypeId === PropertyTypes.SingleFamily ||
    propertyTypeId === PropertyTypes.Condo ||
    propertyTypeId === PropertyTypes.MobileHome
  );
};

export function isSellersCoverageRequiredState(abbreviation: string): boolean {
  return SellersCoverageIncludedStates.indexOf(abbreviation) > -1;
}

export function isProductChannelEligibleForSellersCoverage(channelId: number): boolean {
  return [1, 21].indexOf(channelId) > -1;
}

export function valueIsLessThanN(n: number) {
  return (value: string | number) => !hasValue(value) || +value < n;
}

const defaultErrorMessages = {
  required: 'Required',
  hasValue: 'Required',
  hasDateValue: 'Date Required',
  isAlpha: 'Must contain letters only',
  isPhone: 'Invalid Phone #',
  isValidCity: 'Invalid City',
  isValidCountry: 'Invalid Country',
  isValidStateProvince: 'Invalid State/Province/Territory',
  isValidPhoneNumber: 'Invalid Phone #',
  isValidFaxNumber: 'Invalid Fax #',
  isFax: 'Invalid Fax #',
  isEmail: 'Invalid Email Address',
  isValidEmail: 'Invalid Email Address',
  isZip: 'Invalid ZIP Code',
  isValidZip: 'Invalid ZIP Code',
  hasAlpha: 'Must contain an alphabet character',
  hasNumber: 'Must contain a number',
  checkAlpha: 'Must contain an alphabet character',
  isValidPassword: 'Invalid Password',
  validPassword: 'InvalidPassword',
  isDecimal: 'Number must be a decimal',
  selection: 'Selection Required',
  no3Chars: 'Password cannot share 3 consecutive characters with email address',
  isNumeric: 'Must be a number',
  isFederalTaxId: 'Must be in the format "XXX-XX-XXXX" or "XX-XXXXXXX"',
  checkOnlyAlphaNumeric: 'Must contain only alphanumeric characters',
  hasOnlyAlphaNumeric: 'Must contain only alphanumeric characters',
  checkLength10: 'Must be less than or equal to 10 characters',
  checkLength15: 'Must be less than or equal to 15 characters',
  checkLength20: 'Must be less than or equal to 20 characters',
  checkLength255: 'Must be less than or equal to 255 characters',
  isValidCreditCardNumber: 'Invalid credit card number. Numbers only, no symbols.',
  isValidSecurityCode: 'Invalid security code',
  isValidRoutingNumber: 'Invalid routing number',
  isValidAccountNumber: 'Invalid account number',
  isIntegerGreaterThanZero: 'Must be integer greater than zero',
  isDuplicate: 'This invoice code already exists for this vendor, please enter a different invoice code.',
  isNotFutureYear: 'Year must not be in the future',
  lessThan10000: 'Value must not exceed 9,999.99',
  isSmallInt: 'Value must not exceed 32,767',
  isSmallMoney: 'Value must not exceed ' + smallMoneyMax.toFixed(2)
};

export function getDefaultErrorMessages(override?: {}) {
  if (!override) {
    return defaultErrorMessages;
  }

  var temp = {
    ...defaultErrorMessages
  };

  var overrideKeys = Object.keys(override);
  overrideKeys.forEach(key => {
    temp[key] = override[key];
  });

  return temp;
}



// WEBPACK FOOTER //
// ./src/common/validators.ts